<template>
    <div class="container" style="padding-left: 20px;">
        <div style="width: 450px;">
            <div v-if="result">
                <div v-if="result.email">
                    <h6>User Credentials</h6>
                    <ul class="list list-unstyled">
                        <li>Email: {{ result.email }}</li>
                        <li>Password: {{ result.password }}</li>
                    </ul>
                </div>
                <div v-if="result.activationCode">
                    <h6>Activation Code</h6>
                    <p>{{ applicationTypeText }} — valid until {{ result.validUntilDisplay }}</p>
                    <h4>{{ result.activationCode }}</h4>
                </div>
                <div class="mb-3">
                    <button type="button" class="btn btn-primary" @click="clearForm">New Request</button>
                </div>
            </div>

            <form class="form" @submit.prevent="handleSubmit" v-else>
                <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

                <div class="mb-3">
                    <label class="form-label">Device Code</label>
                    <input type="text" class="form-control" v-model="deviceId" placeholder="Device Code" required />
                </div>

                <div class="mb-3">
                    <label class="form-label">Application</label>
                    <select class="form-select" v-model="applicationTypeId" required>
                        <option disabled value="">Please choose an application</option>
                        <option v-for="app in applications" :key="app.id" :value="app.id">{{ app.name }}</option>
                    </select>
                </div>

                <div class="mb-3">
                    <label class="form-label">OS</label>
                    <select class="form-select" v-model.number="platformId">
                        <option :value="1">Windows</option>
                        <option :value="3">Android</option>
                        <option :value="4">iOS</option>
                        <option :value="6">Linux</option>
                    </select>
                </div>

                <div class="mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                        <label class="form-label mb-0" for="validUntil">Valid until</label>
                        <div class="form-check mb-0">
                            <input
                                id="unlimited"
                                type="checkbox"
                                class="form-check-input"
                                v-model="unlimited"
                            />
                            <label class="form-check-label" for="unlimited">Unlimited</label>
                        </div>
                    </div>
                    <input
                        id="validUntil"
                        type="date"
                        class="form-control"
                        v-model="validUntil"
                        :min="minValidUntil"
                        :disabled="unlimited"
                        :required="!unlimited"
                    />
                </div>

                <div class="mb-3 form-check">
                    <input
                        id="registerInDb"
                        type="checkbox"
                        class="form-check-input"
                        v-model="registerInDb"
                        @change="onRegisterToggle"
                    />
                    <label class="form-check-label" for="registerInDb">Register in DB</label>
                    <div class="form-text">
                        <strong>FOR PRIMARY ECDIS ONLY!</strong> Creates or finds the user, stores License/Installation, returns password.
                    </div>
                </div>

                <div class="mb-3" v-if="registerInDb">
                    <label class="form-label">User Email</label>
                    <input type="email" class="form-control" v-model="email" placeholder="User Email" required />
                </div>

                <div class="mb-3">
                    <button type="submit" class="btn btn-primary" :disabled="submitting">
                        {{ submitting ? 'Working…' : submitLabel }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import myAxios from "@/services/myAxios";

const APPLICATIONS = [
    { id: 101, name: "mKart ECDIS" },
    { id: 102, name: "dKart Navigator" },
    { id: 105, name: "dKart Fishing" },
];

function defaultValidUntil() {
    const d = new Date();
    d.setMonth(d.getMonth() + 12);
    return formatDateInput(d);
}

function unlimitedValidUntil() {
    const d = new Date();
    d.setMonth(d.getMonth() + 700);
    return formatDateInput(d);
}

function formatDateInput(d) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

function formatCode(code) {
    if (!code || code.startsWith("error")) {
        return code;
    }
    return code.replace(/(.{4})/g, "$1-").slice(0, -1);
}

export default {
    name: "DistributorActivation",
    data() {
        return {
            applications: APPLICATIONS,
            deviceId: "",
            applicationTypeId: "",
            platformId: 1,
            validUntil: defaultValidUntil(),
            minValidUntil: formatDateInput(new Date()),
            unlimited: false,
            registerInDb: false,
            email: "",
            result: null,
            error: null,
            submitting: false,
        };
    },
    computed: {
        applicationTypeText() {
            const app = this.applications.find((a) => String(a.id) === String(this.applicationTypeId));
            return app ? app.name : "";
        },
        submitLabel() {
            return this.registerInDb ? "Activate & Register" : "Get Activation Code";
        },
    },
    methods: {
        onRegisterToggle() {
            if (!this.registerInDb) {
                this.email = "";
            }
        },
        clearForm() {
            this.deviceId = "";
            this.applicationTypeId = "";
            this.platformId = 1;
            this.validUntil = defaultValidUntil();
            this.unlimited = false;
            this.registerInDb = false;
            this.email = "";
            this.result = null;
            this.error = null;
        },
        handleSubmit() {
            this.error = null;

            if (!this.deviceId.trim()) {
                this.error = "Device Code is required.";
                return;
            }
            if (!this.applicationTypeId) {
                this.error = "Please choose an application.";
                return;
            }
            if (this.registerInDb && !this.email.trim()) {
                this.error = "Email is required when Register in DB is checked.";
                return;
            }

            const payload = {
                deviceId: this.deviceId.trim(),
                applicationTypeId: Number(this.applicationTypeId),
                platformId: this.platformId,
                validUntil: this.unlimited ? unlimitedValidUntil() : this.validUntil,
            };
            if (this.registerInDb) {
                payload.email = this.email.trim();
            }

            this.submitting = true;
            myAxios
                .post("api/installation/activate-internal", payload)
                .then((resp) => {
                    const data = resp.data;
                    const code = data.activationCode || null;
                    if (code && code.startsWith("error")) {
                        this.error = code;
                        return;
                    }
                    this.result = {
                        email: data.email || null,
                        password: data.password || null,
                        activationCode: formatCode(code),
                        validUntilDisplay: data.validUntil
                            ? String(data.validUntil).slice(0, 10)
                            : this.validUntil,
                    };
                })
                .catch((ex) => {
                    const msg =
                        (ex.response && ex.response.data) ||
                        ex.message ||
                        "Activation failed.";
                    this.error = typeof msg === "string" ? msg : JSON.stringify(msg);
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
    },
};
</script>
